import React from 'react';
import { Row, Col } from 'reactstrap';
import Box from '@mui/material/Box';
import {
    Block,
    FlexDiv,
    Heading2,
    Heading3,
    InlineProgressBar,
    SpaceBetweenDiv,
    TextDefault,
    TextTiny,
    TextWithTooltipIcon,
    palette,
} from '@awarego-ui/components';
import styled from 'styled-components';
import { calculateColor, defaultZero } from '../../../../utils/helpers';
import ThemedSpinner from '../../../../components/themed/ThemedSpinner';
import SubHeaderSeparator from '../../../../components/header/SubHeaderSeparator';
import LessonsDeliveredDonut from '../LessonsDeliveredDonut';
import ProgressChart from '../ProgressChart';

const HugeHeading = styled.h1`
    font-size: 56px;
    font-weight: 700;
    text-align: center;
`;

function TrainingOverview({
    editingAutomation,
    chartMetrics,
    chartMetricsLoading,
    chartMetricsMigrating,
    score,
    attendance,
    statSent,
    statSentChange,
    showRemindersModal,
    handleSwitchSyncLists,
    handleManualSyncLists,
    store,
}) {
    if (chartMetricsLoading) return <ThemedSpinner />;

    return (
        <Row>
            <Col
                md="3"
                xs="12"
                style={{ display: 'flex', flexDirection: 'column' }}
            >
                <Block>
                    <Row>
                        <Col md="12" xs="12">
                            <TextWithTooltipIcon
                                textComponent={<Heading2>Lessons delivered</Heading2>}
                                tooltipText="Lessons that have already been sent."
                            />
                        </Col>
                        <Col md="12" xs="12">
                            <LessonsDeliveredDonut
                                lessonsDelivered={editingAutomation.lessonsSent}
                                lessonsTotal={editingAutomation.lessonsTotal}
                                chartData={chartMetrics}
                            />
                        </Col>
                    </Row>
                </Block>
                <Block gap={16}>
                    <Heading3>Invited employees</Heading3>
                    <HugeHeading>{editingAutomation.totalUsers}</HugeHeading>
                </Block>
                <Block gap={16} flexGrow={1}>
                    <TextWithTooltipIcon
                        textComponent={<Heading3>Employee list sync</Heading3>}
                        tooltipText={
                            editingAutomation.disableSyncLists
                                ? 'User sync is inactive. Changes to the employee lists will not be automatically updated in this training.'
                                : 'User sync is active. Users added to user lists in this training are automatically added to this training.'
                        }
                    />
                    <SpaceBetweenDiv>
                        <Heading2
                            style={{
                                borderBottom: store.isAdmin
                                    ? `1px dotted ${
                                          editingAutomation.disableSyncLists
                                              ? palette.vibrant.orange
                                              : palette.evergreen.aspargus
                                      }`
                                    : undefined,
                            }}
                            link={store.isAdmin || undefined}
                            onClick={
                                store.isAdmin ? handleSwitchSyncLists : undefined
                            }
                            color={
                                editingAutomation.disableSyncLists
                                    ? palette.vibrant.orange
                                    : palette.evergreen.aspargus
                            }
                        >
                            {editingAutomation.disableSyncLists
                                ? 'Inactive'
                                : 'Active'}
                        </Heading2>
                        {editingAutomation.disableSyncLists && store.isAdmin && (
                            <>
                                <SubHeaderSeparator />
                                <TextTiny link onClick={handleManualSyncLists}>
                                    Sync users
                                </TextTiny>
                            </>
                        )}
                    </SpaceBetweenDiv>
                </Block>
            </Col>
            <Col md="9" xs="12">
                <Row>
                    <Col md="6" xs="12">
                        <Block>
                            <SpaceBetweenDiv gap={8} rowGap={4}>
                                <TextWithTooltipIcon
                                    textComponent={<Heading2>Completion rate</Heading2>}
                                    tooltipText="The score improves once your employees watch the assigned training videos and successfully complete the training quizzes."
                                />
                                {editingAutomation.canRemind && store.isAdmin && (
                                    <TextDefault
                                        themed
                                        link
                                        bold
                                        onClick={showRemindersModal}
                                    >
                                        Send reminders
                                    </TextDefault>
                                )}
                            </SpaceBetweenDiv>
                            <FlexDiv gap={8} mt={16}>
                                <TextTiny
                                    themed={statSent === 'sent'}
                                    link
                                    bold
                                    onClick={() => statSentChange('sent')}
                                >
                                    Sent Lessons
                                </TextTiny>
                                <TextTiny
                                    themed={statSent === 'all'}
                                    link
                                    bold
                                    onClick={() => statSentChange('all')}
                                >
                                    All lessons
                                </TextTiny>
                            </FlexDiv>
                            <p className="mtb20">
                                <strong>
                                    {defaultZero(score).toFixed(0)}% of
                                    {statSent === 'sent'
                                        ? ' sent '
                                        : ' assigned '}
                                    training has been completed
                                </strong>
                            </p>
                            <FlexDiv $noWrap>
                                <FlexDiv fullWidth alignCenter>
                                    <InlineProgressBar
                                        customColor={calculateColor(score)}
                                        value={score}
                                        fullWidth
                                    />
                                </FlexDiv>
                                <FlexDiv flexGrow={1}>
                                    <Heading2 color={calculateColor(score)}>
                                        {defaultZero(score).toFixed(0)}/100
                                    </Heading2>
                                </FlexDiv>
                            </FlexDiv>
                        </Block>
                    </Col>
                    <Col md="6" xs="12">
                        <Block>
                            <TextWithTooltipIcon
                                textComponent={<Heading2>Attendance</Heading2>}
                                tooltipText="This is the number of everyone who has logged in, regardless if they finished a lesson."
                            />
                            <Box height={45} />
                            <p className="mtb20">
                                <strong>
                                    {editingAutomation._rawData.opened || '0'} employees
                                    logged in
                                </strong>{' '}
                                out of {editingAutomation._rawData.totalUsers}
                            </p>
                            <FlexDiv $noWrap>
                                <FlexDiv fullWidth alignCenter>
                                    <InlineProgressBar
                                        customColor={calculateColor(attendance)}
                                        value={attendance}
                                        fullWidth
                                    />
                                </FlexDiv>
                                <FlexDiv flexGrow={1}>
                                    <Heading2 color={calculateColor(attendance)}>
                                        {editingAutomation._rawData.opened || '0'}/
                                        {editingAutomation._rawData.totalUsers}
                                    </Heading2>
                                </FlexDiv>
                            </FlexDiv>
                        </Block>
                    </Col>
                </Row>
                <Block>
                    {chartMetricsMigrating ? (
                        <Heading2>Preparing data</Heading2>
                    ) : (
                        <ProgressChart chartData={chartMetrics} />
                    )}
                </Block>
            </Col>
        </Row>
    );
}

export default TrainingOverview;
